"use client";

import React from "react";
import { SectionContentProps } from "../../../types";
import { FooterContent, SocialLink, LinkGroup } from "../types";
import { SocialLinks, LinkGroupComponent, Copyright, getSocialIcon } from "../BaseFooter";
import { colors, footerStyles, layout, spacing } from "../tokens";

// ============================================================
// BIG BRAND FOOTER - Brand-focused with large logo area
// ============================================================
// Best for: Creative agencies, E-commerce, Consumer brands
// Features: Large brand presence, visual hierarchy, CTAs
// ============================================================

interface BigBrandFooterProps extends SectionContentProps {
  content: FooterContent;
}

export const BigBrandFooter: React.FC<BigBrandFooterProps> = ({
  content: propContent,
  onContentChange,
  isSelected,
}) => {
  const content = propContent || {};
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  // Dynamic colors with fallbacks
  const backgroundColor = content.backgroundColor || "#0f172a"; // slate-900
  const textColor = content.textColor || "#ffffff";
  const accentColor = content.accentColor || "#3b82f6"; // blue-500

  const socialLinks: SocialLink[] = content.socialLinks || [
    { platform: "instagram", url: "#" },
    { platform: "twitter", url: "#" },
    { platform: "linkedin", url: "#" },
    { platform: "dribbble", url: "#" },
  ];

  const defaultLinkGroups: LinkGroup[] = [
    {
      title: "Solutions",
      links: [
        { label: "For Enterprise", href: "#" },
        { label: "For Startups", href: "#" },
        { label: "For Agencies", href: "#" },
        { label: "For Government", href: "#" },
      ],
    },
    {
      title: "Support",
      links: [
        { label: "Help Center", href: "#" },
        { label: "Contact Us", href: "#" },
        { label: "System Status", href: "#" },
        { label: "API Status", href: "#" },
      ],
    },
  ];

  const linkGroups = content.linkGroups || defaultLinkGroups;

  return (
    <footer
      className={`${isSelected ? "ring-2 ring-blue-500" : ""}`}
      style={{
        fontFamily,
        background: content.backgroundColor
          ? backgroundColor
          : `linear-gradient(to bottom, #0f172a, #020617)`,
        color: textColor
      }}
      role="contentinfo"
      aria-label="Big brand footer"
    >
      {/* Main Brand Section */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        {/* Large Brand Area */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            {content.logo && (
              <img
                src={content.logo}
                alt="Logo"
                className="h-16 w-auto brightness-0 invert"
              />
            )}
          </div>
          {content.tagline && (
            <p className="text-blue-400 text-lg font-medium mb-3">
              {content.tagline}
            </p>
          )}
          {content.description && (
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              {content.description}
            </p>
          )}

          {/* CTA Buttons */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-500 transition-all hover:scale-105">
              Get Started
            </button>
            <button className="px-8 py-3 bg-transparent border border-slate-600 text-white font-semibold rounded-xl hover:bg-slate-800 transition-all">
              Contact Sales
            </button>
          </div>
        </div>

        {/* Links Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-12 mb-16">
          {linkGroups.map((group, index) => (
            <div key={index}>
              <h4 className="text-white font-bold text-lg mb-6">{group.title}</h4>
              <ul className="space-y-4" role="list">
                {group.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link.href}
                      className="text-slate-400 hover:text-white transition-colors"
                      target={link.isExternal ? "_blank" : undefined}
                      rel={link.isExternal ? "noopener noreferrer" : undefined}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6">Get in Touch</h4>
            <ul className="space-y-4 text-slate-400">
              <li className="flex items-center gap-3">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span>Live Chat Available</span>
              </li>
              <li>
                <a href="mailto:hello@example.com" className="hover:text-white transition-colors">
                  hello@example.com
                </a>
              </li>
              <li>
                <a href="tel:+1234567890" className="hover:text-white transition-colors">
                  +1 (234) 567-890
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-800"></div>

        {/* Bottom Bar */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6 pt-8">
          <Copyright
            text={content.copyright || "© 2026 INSTACKS. All rights reserved."}
            className="text-slate-500"
          />

          {/* Social Links */}
          <SocialLinks links={socialLinks} size="md" />

          {/* Legal Links */}
          <nav aria-label="Legal links">
            <ul className="flex items-center gap-6 text-sm text-slate-400">
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Terms of Service
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Cookie Settings
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default BigBrandFooter;
